import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import VendorHeader from './components/VendorHeader';
import TabNavigation from './components/TabNavigation';
import OverviewTab from './components/OverviewTab';
import CompanyDetailsTab from './components/CompanyDetailsTab';
import BankInformationTab from './components/BankInformationTab';
import DocumentsTab from './components/DocumentsTab';
import ComplianceTab from './components/ComplianceTab';
import AgreementsTab from './components/AgreementsTab';
import ActivityHistoryTab from './components/ActivityHistoryTab';
import { vendorService, approvalService } from '../../services/apiService';
import { useToast } from '../../components/ui/Toast';

const VendorProfileDetails = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [vendor, setVendor] = useState(null);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock user role - in real app this would come from auth context
  const userRole = 'Admin'; // Admin, Approver, Viewer

  // Fetch vendor data and approval history from API
  useEffect(() => {
    const fetchVendorData = async () => {
      try {
        setLoading(true);
        
        // Fetch vendor data
        const vendorResponse = await vendorService.getVendorById(vendorId);
        // Backend returns { success: true, data: vendor }
        const vendorData = vendorResponse.data || vendorResponse;
        setVendor(vendorData);
        
        // Fetch approval history
        try {
          const approvalResponse = await approvalService.getVendorHistory(vendorId);
          // Backend returns { success: true, data: approvals }
          const approvalData = approvalResponse.data || approvalResponse;
          setApprovalHistory(Array.isArray(approvalData) ? approvalData : []);
        } catch (approvalErr) {
          console.warn('Error fetching approval history:', approvalErr);
          setApprovalHistory([]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching vendor data:', err);
        if (err.response?.status === 401) {
          setError('Authentication required. Please log in.');
        } else if (err.response?.status === 404) {
          setError('Vendor not found.');
        } else {
          setError('Failed to load vendor data. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (vendorId) {
      fetchVendorData();
    }
  }, [vendorId]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'company', label: 'Company Details', icon: 'Building2' },
    { id: 'bank', label: 'Bank Information', icon: 'CreditCard' },
    { id: 'documents', label: 'Documents', icon: 'FileText', badge: vendor?.document_count > 0 ? vendor.document_count : null },
    { id: 'agreements', label: 'Agreements', icon: 'FileSignature', badge: vendor?.agreement_count > 0 ? vendor.agreement_count : null },
    { id: 'activity', label: 'Activity History', icon: 'Activity' }
  ];

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignCode, setAssignCode] = useState('');

  const handleEdit = () => {
    // Open Assign Vendor Code modal
    setAssignCode('');
    setShowAssignModal(true);
  };

  const handleStatusChange = async (nextStatus) => {
    try {
      const updated = await vendorService.changeStatus(vendorId, nextStatus);
      // Backend returns { success: true, data: vendor }
      const vendorData = updated.data || updated;
      setVendor(vendorData);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error('Authentication required. Please log in.');
        window.location.href = '/company-user-login';
        return;
      }
      const errorMessage = e.response?.data?.error?.message 
        || e.response?.data?.detail 
        || e.message 
        || 'Failed to update status';
      toast.error(errorMessage);
    }
  };

  const submitAssignVendorCode = async () => {
    try {
      await vendorService.assignVendorCode(vendorId, assignCode || null);
      // Refresh vendor data
      const refreshed = await vendorService.getVendorById(vendorId);
      // Backend returns { success: true, data: vendor }
      const vendorData = refreshed.data || refreshed;
      setVendor(vendorData);
      setShowAssignModal(false);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error('Authentication required. Please log in.');
        window.location.href = '/company-user-login';
        return;
      }
      const errorMessage = e.response?.data?.error?.message 
        || e.response?.data?.detail 
        || e.message 
        || 'Failed to assign vendor code';
      toast.error(errorMessage);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab vendor={vendor} />;
      case 'company':
        return <CompanyDetailsTab vendor={vendor} userRole={userRole} approvalHistory={approvalHistory} />;
      case 'bank':
        return <BankInformationTab vendor={vendor} />;
      case 'documents':
        return <DocumentsTab vendor={vendor} userRole={userRole} />;
      case 'agreements':
        return <AgreementsTab vendor={vendor} />;
      case 'activity':
        return <ActivityHistoryTab vendor={vendor} approvalHistory={approvalHistory} />;
      default:
        return <OverviewTab vendor={vendor} />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
                <p className="mt-4 text-slate-600">Loading vendor profile...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-5">
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="AlertCircle" size={48} className="text-red-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">Error Loading Data</h3>
                <p className="text-slate-600 mb-6">{error}</p>
                <Button onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="space-y-5">
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="FileText" size={48} className="text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">Vendor Not Found</h3>
                <p className="text-slate-600">The requested vendor could not be found.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
          <div className="mb-6">
            <Breadcrumb 
              items={[
                { label: 'Dashboard', path: '/dashboard-overview' },
                { label: 'Vendors', path: '/vendor-master-list' },
                { label: vendor.company_name || 'Vendor Details', path: `/vendor-profile-details/${vendor.id}` }
              ]} 
            />
          </div>

          <VendorHeader 
            vendor={vendor}
            onEdit={handleEdit}
            onStatusChange={handleStatusChange}
            userRole={userRole}
          />

          {showAssignModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <Card className="w-full max-w-md border border-slate-200 shadow-sm bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Assign Vendor Code</h3>
                  <p className="text-sm text-slate-600 mb-4">Enter a unique vendor code or leave blank to auto-generate.</p>
                  <input
                    type="text"
                    value={assignCode}
                    onChange={(e) => setAssignCode(e.target.value)}
                    placeholder="e.g. VND0001"
                    className="w-full border border-slate-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 transition-all text-sm"
                  />
                  <div className="flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowAssignModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      onClick={submitAssignVendorCode}
                    >
                      Assign
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <TabNavigation 
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabs={tabs}
          />

          <div className="min-h-96">
            {renderTabContent()}
          </div>
    </div>
  );
};

export default VendorProfileDetails;